import React, { useMemo, useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  OutlinedInput,
  InputAdornment,
  Chip,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { STATE_OPTIONS } from '../../utils/stateLegislationApi';
import {
  BALLOT_SCOPE_CHIP_OPTIONS,
  normalizeBallotScope,
} from '../../utils/ballotMeasuresApi';

const PRIMARY = '#047857';

function ToggleChip({ label, active, onClick }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        fontWeight: 600,
        fontSize: 13,
        cursor: 'pointer',
        bgcolor: active ? PRIMARY : '#fff',
        color: active ? '#fff' : '#0f172a',
        border: active ? `1px solid ${PRIMARY}` : '1px solid #cbd5e1',
        '&:hover': {
          bgcolor: active ? '#065f46' : '#f1f5f9',
          borderColor: active ? '#065f46' : '#94a3b8',
        },
      }}
    />
  );
}

export default function BallotMeasuresFilterModal({
  open,
  onClose,
  draftStateCode,
  onDraftStateChange,
  draftScope,
  onDraftScopeChange,
  userState,
  onApply,
}) {
  const [showStateList, setShowStateList] = useState(false);
  const [stateSearch, setStateSearch] = useState('');

  const stateOptions = useMemo(
    () => STATE_OPTIONS.filter((s) => s.code !== 'FED'),
    [],
  );

  const orderedStates = useMemo(() => {
    const ordered = [];
    if (userState) {
      const userOpt = stateOptions.find((s) => s.code === userState);
      if (userOpt) ordered.push(userOpt);
    }
    stateOptions.forEach((s) => {
      if (s.code !== userState) ordered.push(s);
    });
    return ordered;
  }, [stateOptions, userState]);

  const filteredStates = useMemo(() => {
    if (!stateSearch.trim()) return orderedStates;
    const q = stateSearch.toLowerCase();
    return orderedStates.filter(
      (s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q),
    );
  }, [orderedStates, stateSearch]);

  const draftStateName =
    stateOptions.find((s) => s.code === draftStateCode)?.name || 'Select state';

  React.useEffect(() => {
    if (!open) {
      setShowStateList(false);
      setStateSearch('');
    }
  }, [open]);

  if (showStateList) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: '#fff',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            pt: 2,
            pb: 1.5,
            borderBottom: '1px solid #e2e8f0',
            flexShrink: 0,
          }}
        >
          <Box
            component="button"
            onClick={() => {
              setShowStateList(false);
              setStateSearch('');
            }}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0.5,
              display: 'flex',
              borderRadius: '50%',
              '&:hover': { bgcolor: '#f1f5f9' },
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 22, color: '#334155' }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a', flex: 1 }}>
            Select state
          </Typography>
        </Box>

        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <OutlinedInput
            size="small"
            fullWidth
            placeholder="Search states…"
            value={stateSearch}
            onChange={(e) => setStateSearch(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
              </InputAdornment>
            }
            sx={{
              borderRadius: '10px',
              fontSize: 14,
              bgcolor: '#fff',
              '& fieldset': { borderColor: '#cbd5e1' },
              '&:hover fieldset': { borderColor: '#94a3b8' },
              '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1 },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
          {filteredStates.map((st) => {
            const isActive = draftStateCode === st.code;
            const isUserState = st.code === userState;
            return (
              <Box
                key={st.code}
                component="button"
                onClick={() => {
                  onDraftStateChange?.(st.code);
                  setShowStateList(false);
                  setStateSearch('');
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  background: isActive ? '#f0fdf4' : 'none',
                  border: 'none',
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 14,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? PRIMARY : '#0f172a',
                    textAlign: 'left',
                  }}
                >
                  {st.name}
                  {isUserState && (
                    <Typography component="span" sx={{ fontSize: 12, color: '#64748b', ml: 1 }}>
                      (Your state)
                    </Typography>
                  )}
                </Typography>
                {isActive && <CheckRoundedIcon sx={{ fontSize: 20, color: PRIMARY }} />}
              </Box>
            );
          })}
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          bgcolor: '#fff',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2.5,
          pb: 1.5,
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
          Filter ballot measures
        </Typography>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0.5,
            display: 'flex',
            borderRadius: '50%',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 22, color: '#334155' }} />
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 1 }}>
            State
          </Typography>
          <Box
            component="button"
            onClick={() => setShowStateList(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
              borderRadius: '10px',
              border: '1px solid #cbd5e1',
              bgcolor: '#fff',
              cursor: 'pointer',
              '&:hover': { borderColor: '#94a3b8' },
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
              {draftStateName}
            </Typography>
            <Typography sx={{ fontSize: 13, color: PRIMARY, fontWeight: 600 }}>
              Change
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 1 }}>
            Level
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {BALLOT_SCOPE_CHIP_OPTIONS.map((opt) => (
              <ToggleChip
                key={opt.id}
                label={opt.label}
                active={normalizeBallotScope(draftScope) === opt.id}
                onClick={() => onDraftScopeChange?.(opt.id)}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: '1px solid #e2e8f0',
          flexShrink: 0,
        }}
      >
        <Box
          component="button"
          onClick={onClose}
          sx={{
            flex: 1,
            py: 1.25,
            borderRadius: '10px',
            border: '1px solid #cbd5e1',
            bgcolor: '#fff',
            fontWeight: 600,
            fontSize: 15,
            color: '#0f172a',
            cursor: 'pointer',
            '&:hover': { bgcolor: '#f8fafc' },
          }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={() => {
            onApply?.();
            onClose();
          }}
          disabled={!draftStateCode}
          sx={{
            flex: 1,
            py: 1.25,
            borderRadius: '10px',
            border: 'none',
            bgcolor: draftStateCode ? PRIMARY : '#94a3b8',
            fontWeight: 700,
            fontSize: 15,
            color: '#fff',
            cursor: draftStateCode ? 'pointer' : 'not-allowed',
            '&:hover': { bgcolor: draftStateCode ? '#065f46' : '#94a3b8' },
          }}
        >
          Apply
        </Box>
      </Box>
    </Dialog>
  );
}
